import React, { useState, useRef, useEffect, useContext } from "react";
import {
  Card,
  Button,
  Typography,
  message,
  Progress,
  Modal,
  Spin,
  Pagination,
  Select,
  Input,
} from "antd";
import {
  UploadOutlined,
  CheckCircleTwoTone,
  DeleteOutlined,
  SearchOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import httpInjectorService from "../../services/http-injector.service";
import { NotificationContext } from "../../NotificationProvider";
import { useNavigate } from "react-router";
import { useRefresh } from "../../RefreshContext";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import {
  Modal as RSModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons";


const Paysheet = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploadDates, setUploadDates] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { refreshKey, triggerRefresh } = useRefresh();
  const itemsPerPage = 5;
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { openNotification } = useContext(NotificationContext);
  const [sortOrder, setSortOrder] = useState("desc");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const handleButtonClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("file", selectedFile);

    let simulatedProgress = 0;
    let progressInterval;

    try {
      setUploading(true);
      setUploadProgress(0);

      progressInterval = setInterval(() => {
        simulatedProgress += 10;
        if (simulatedProgress <= 95) {
          setUploadProgress(simulatedProgress);
        }
      }, 300);

      const response = await httpInjectorService.uploadPaySheet(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.status === "SUCCESS") {
        openNotification("success", response.message);
        await getPaysheetUploadedDates();
      } else {
        openNotification("error", response.message);
        throw new Error("Upload failed");
      }
    } catch (error) {
      console.error("Upload error:", error);
      message.error(`${selectedFile.name} failed to upload`);
    } finally {
      clearInterval(progressInterval);
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 1000);
      event.target.value = null;
    }
  };

  const getPaysheetUploadedDates = async () => {
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const response = await httpInjectorService.paysheetUploadedDates();
      if (response.status === "SUCCESS") {
        const sorted = response.data.sort(
          (a, b) => b.created_at - a.created_at
        );
        setUploadDates(sorted);
      } else {
        setUploadDates([]);
      }
    } catch (error) {
      console.error("Failed to fetch upload dates:", error);
      setUploadDates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const response = await httpInjectorService.deletePaysheet({
        file_id: id,
      });
      if (response.status === "SUCCESS") {
        openNotification("success", response.message);
        await getPaysheetUploadedDates();
      } else {
        openNotification("error", response.message);
      }
    } catch (error) {
      console.error("Delete error:", error);
      message.error("Error deleting paysheet");
    }
  };

  const handleClick = (id, filename) => {
    navigate(`/paysheetDetails/${id}`, {
      state: { filename },
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const filteredData = uploadDates
    .filter((item) => {
      const fileNameMatch = item.pay_sheet_file_name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const uploadedDateFormatted = formatDate(item.uploaded_date);
      const dateMatch = uploadedDateFormatted.includes(searchTerm);

      return fileNameMatch || dateMatch;
    })
    .sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return sortOrder === "desc"
        ? dateB - dateA // latest first
        : dateA - dateB; // oldest first
    });

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    getPaysheetUploadedDates();
  }, [refreshKey]);

  const handleDownloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Template");

    // Constant headers
    const headers = [
      "FIRST NAME",
      "LAST NAME",
      "WORK NUMBER",
      "TAX CODE",
      "PAY PERIOD",
      "PAY DATE",
      "PAY TYPE",
      "NI NUMBER",
      "PAYMENT SUMMARY",
      "TOTAL PAYMENTS",
      "NET PAY",
    ];

    // Set headers as worksheet columns
    worksheet.columns = headers.map((header) => ({
      header: header,
      key: header.toLowerCase().replace(/ /g, "_"), // internal key format
      width: 20,
    }));

    // Optionally, freeze header row
    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    // Export to Excel buffer
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const fileName = "Paysheet_Template.xlsx";
    saveAs(blob, fileName);
  };

  const confirmDelete = (id) => {
    setSelectedDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const cancelDelete = () => {
    setIsDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  const proceedDelete = async () => {
    if (!selectedDeleteId) return;
    await handleDelete(selectedDeleteId);
    setIsDeleteModalOpen(false);
    setSelectedDeleteId(null);
  };

  return (
    <div>
      <Card
        title="Documents"
        bordered
        extra={
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div className="position-relative">
              <i
                className="bx bx-search-alt"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "18px",
                  color: "#aaa",
                }}
              ></i>
              <input
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearchTerm(e.target.value);
                }}
                id="search-bar-0"
                type="text"
                className="form-control ps-5"
                placeholder={`${filteredData.length} record${
                  filteredData.length !== 1 ? "s" : ""
                }...`}
                value={searchTerm || ""}
                style={{ height: "38px", minWidth: "200px", maxWidth: "300px" }}
              />
            </div>
            <Select
              defaultValue="desc"
              style={{ width: 200 }}
              onChange={(value) => setSortOrder(value)}
              options={[
                {
                  value: "desc",
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <ArrowDownOutlined style={{ color: "#5167c1" }} />
                      Newest
                    </span>
                  ),
                },
                {
                  value: "asc",
                  label: (
                    <span
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <ArrowUpOutlined style={{ color: "#5167c1" }} />
                      Oldest 
                    </span>
                  ),
                },
              ]}
            />
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              style={{ backgroundColor: "#5167c1", color: "white" }}
            >
              Template
            </Button>
          </div>
        }
        style={{
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          width: "100%",
          minHeight: "600px",
        }}
      >
        {currentPage === 1 && (
          <Card
            title="Upload Paysheet"
            style={{
              margin: "0 auto 30px auto",
              borderRadius: "8px",
              maxWidth: "600px",
              boxShadow: "0 1px 6px rgba(0, 0, 0, 0.08)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "24px",
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <Button
                icon={<UploadOutlined style={{ color: "white" }} />}
                size="large"
                style={{
                  backgroundColor: "#5167c1",
                  borderColor: "#5167c1",
                  borderRadius: "6px",
                  marginBottom: "8px",
                  color: "white",
                }}
                onClick={handleButtonClick}
                disabled={uploading}
              />
              {uploading && (
                <Progress
                  percent={uploadProgress}
                  showInfo
                  status="active"
                  strokeColor="#52c41a"
                  style={{ width: 200, marginBottom: 8 }}
                />
              )}
              <Typography.Text style={{ fontSize: "14px", color: "#555" }}>
                Drop or drag, choose file to upload
              </Typography.Text>
            </div>
          </Card>
        )}

        {/* Uploaded Paysheets */}
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="default" tip="Loading paysheets..." />
          </div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
              }}
            >
              {paginatedData.map((item, index) => (
                <Card
                  key={index}
                  style={{
                    borderRadius: "8px",
                    boxShadow: "0 1px 6px rgba(0, 0, 0, 0.08)",
                    padding: "14px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 12 }}
                    >
                      <CheckCircleTwoTone
                        twoToneColor="#52c41a"
                        style={{ fontSize: "24px" }}
                      />
                      <Typography.Link
                        style={{ fontSize: "18px" }}
                        onClick={() =>
                          handleClick(item.id, item.pay_sheet_file_name)
                        }
                      >
                        {item.pay_sheet_file_name}
                      </Typography.Link>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <Typography.Text type="secondary">
                        uploaded on {formatDate(item.uploaded_date)}
                      </Typography.Text>
                      <DeleteOutlined
                        onClick={() => confirmDelete(item.id)}
                        style={{
                          color: "red",
                          fontSize: "18px",
                          cursor: "pointer",
                        }}
                        title="Delete paysheet"
                      />
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            <div
              style={{
                textAlign: "center",
                marginTop: "20px",
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={itemsPerPage}
                total={filteredData.length}
                onChange={(page) => setCurrentPage(page)}
                showSizeChanger={false}
              />
            </div>
          </>
        )}
      </Card>
      <RSModal isOpen={isDeleteModalOpen} toggle={cancelDelete} centered>
        <ModalHeader toggle={cancelDelete}>Confirm Deletion</ModalHeader>
        <ModalBody>Are you sure you want to delete this paysheet?</ModalBody>
        <ModalFooter>
          <Button onClick={proceedDelete}>Yes</Button>
          <Button onClick={cancelDelete}>No</Button>
        </ModalFooter>
      </RSModal>
    </div>
  );
};

export default Paysheet;
